import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import ButtonCore from '../../Button/ButtonCore/ButtonCore';
import ButtonIcon from '../../ButtonIcon/ButtonIcon';
import Icon from '../../Icons/Icon';
import InputCore from '../InputCore/InputCore';
import TextareaCore from '../TextareaCore/TextareaCore';

import './InputField.css';

const SIZES = {
  small: 'small',
  large: 'large',
};

function InputField({
  id,
  name,
  placeholder,
  kind,
  size,
  type,
  label,
  className,
  isRequired,
  isCritical,
  isReadOnly,
  CriticalHelperText,
  HelperText,
  isDisabled,
  prefixIcon,
  suffixButtonType,
  suffixIsToggle,
  suffixOnClick,
  backgroundColor,
  value,
  setValue,
  setIsCritical,
  tabIndex,
}) {
  const [isUsingMouse, setIsUsingMouse] = useState(false);

  const handleMouseDown = () => {
    setIsUsingMouse(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      setIsUsingMouse(false);
    }
  };

  const addEventListeners = () => {
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
  };

  const removeEventListeners = () => {
    document.removeEventListener('mousedown', handleMouseDown);
    document.removeEventListener('keydown', handleKeyDown);
  };

  useEffect(() => {
    addEventListeners();

    return removeEventListeners;
  }, []);

  const inputClass = classnames(
    'InputField-input',
    `InputField--${size}`,
    `InputField--${kind}`,
    `InputField--${backgroundColor}`,
    prefixIcon && `InputField-input-prefixIcon--${size}`,
    suffixButtonType && `InputField-input-suffixButton`,
    isDisabled && `InputField-input--isDisabled`,
    isCritical && `InputField-input--isCritical`,
    isReadOnly && `InputField-input--isReadOnly`,
    isUsingMouse && 'InputField-noOutline',
    className
  );
  const multilineClass = classnames(
    'InputField-input',
    `InputField--${kind}`,
    `InputField--${backgroundColor}`,
    isDisabled && `InputField-input--isDisabled`,
    isCritical && `InputField-input--isCritical`,
    isReadOnly && `InputField-input--isReadOnly`,
    isUsingMouse && 'InputField-noOutline',
    className
  );
  const closeClass = classnames(
    `InputField-closeButtonContainer`,
    kind === 'multiline'
      ? 'InputField-closeButtonContainer--multiline'
      : `InputField-closeButtonContainer--${size}`,
    suffixButtonType && `InputField-closeButtonContainer--suffixButton`,
    isReadOnly && `InputField-closeButtonContainer--isReadOnly`
  );
  const suffixButtonClass = classnames(
    'InputField-suffixButton',
    isRequired && isCritical && 'InputField-suffixButton--isCritical',
    isDisabled && 'InputField-suffixButton--isDisabled',
    isReadOnly && `InputField-suffixButton--isReadOnly`
  );

  const onInputChange = (event) => {
    setValue(event.target.value);
    if (setIsCritical) setIsCritical(false);
  };

  const clearValue = () => {
    setValue('');
  };

  const getSuffixButtonIcon = () => {
    switch (suffixButtonType) {
      case 'password':
        if (!suffixIsToggle) {
          return (
            <Icon
              className='InputField-suffixButton-icon'
              kind='VisabilityOn'
            />
          );
        }

        return (
          <Icon className='InputField-suffixButton-icon' kind='VisabilityOff' />
        );
      case 'date':
        return (
          <Icon className='InputField-suffixButton-icon' kind='calendar' />
        );
      case 'dropdown':
        return (
          <div className='InputField-suffixButton-icon'>
            <Icon
              className='InputField-suffixButton-icon'
              kind='ChevronDown'
              color='navy-700'
              rotate={suffixIsToggle ? '180' : null}
            />
          </div>
        );
      case 'timePicker':
        return <Icon className='InputField-suffixButton-icon' kind='Time' />;
      default:
        return null;
    }
  };

  return (
    <div className='InputField-Container'>
      <div className={`InputField InputField-${kind}`}>
        {/* Label Container */}
        <div className='InputField-labelContainer'>
          {/* isRequired */}
          {isRequired && (
            <div className='InputField--isRequired'>
              {value.length > 0 ? null : (
                <div
                  className={`InputField--isRequired--${
                    isCritical ? 'isCritical' : 'isWarning'
                  }`}>
                  *
                </div>
              )}
            </div>
          )}
          {/* Label */}
          {kind !== 'search' && <span htmlFor={label}>{label}</span>}
        </div>
        <div className='InputField-inputContainer'>
          {kind === 'multiline' ? (
            <TextareaCore
              name={name}
              id={id}
              placeholder={placeholder}
              className={multilineClass}
              autoComplete='off'
              onChange={onInputChange}
              value={value}
              isDisabled={isDisabled}
              isRequired={isRequired}
              ariaDescribedBy={placeholder}
              rows={4}
              tabIndex={isDisabled ? -1 : tabIndex}
            />
          ) : (
            <InputCore
              name={name}
              id={id}
              type={type}
              placeholder={placeholder}
              className={inputClass}
              autoComplete='off'
              onChange={onInputChange}
              value={value}
              isDisabled={isDisabled}
              isRequired={isRequired}
              ariaDescribedBy={placeholder}
              onMouseDown={handleMouseDown}
              onKeyDown={handleKeyDown}
              tabIndex={isDisabled ? -1 : tabIndex}
            />
          )}
          {prefixIcon && (
            <i
              className={`InputField-prefixIcon InputField-prefixIcon--${size}`}>
              {prefixIcon}
            </i>
          )}
          {value.length > 0 && !isDisabled && kind !== 'multiline' && (
            <div className={closeClass}>
              <ButtonIcon
                size='small'
                kind='ghost'
                tabIndex={tabIndex}
                className='InputField-closeButtonIcon'
                // TODO: Fix with neutral-000 later with correct token
                icon={<Icon kind='close' color='neutral-100' size='xSmall' />}
                onClick={() => clearValue()}
              />
            </div>
          )}
          {suffixButtonType && (
            <ButtonCore
              type='button'
              tabIndex={isDisabled ? -1 : tabIndex}
              onClick={() => suffixOnClick()}
              isDisabled={isDisabled}
              className={suffixButtonClass}>
              {getSuffixButtonIcon()}
              <span
                aria-hidden='true'
                className={classnames(
                  'InputField-suffixButton-overlay',
                  isDisabled && 'InputField-suffixButton-overlay--isDisabled'
                )}
              />
            </ButtonCore>
          )}
        </div>
        <div>
          {isCritical && CriticalHelperText}
          {HelperText && HelperText}
        </div>
      </div>
    </div>
  );
}

InputField.defaultProps = {
  id: null,
  type: 'text',
  kind: 'singleline',
  size: 'large',
  className: '',
  label: '',
  isDisabled: false,
  isRequired: false,
  isCritical: false,
  isReadOnly: false,
  CriticalHelperText: null,
  HelperText: null,
  prefixIcon: null,
  suffixButtonType: null,
  suffixIsToggle: false,
  suffixOnClick: null,
  backgroundColor: 'neutral',
  value: '',
  setValue: null,
  setIsCritical: null,
  tabIndex: 0,
};

InputField.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  kind: PropTypes.oneOf(['search', 'singleline', 'multiline']),
  size: PropTypes.oneOf(Object.values(SIZES)),
  type: PropTypes.string,
  label: PropTypes.string,
  isDisabled: PropTypes.bool,
  className: PropTypes.string,
  isRequired: PropTypes.bool,
  isCritical: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  prefixIcon: PropTypes.node,
  CriticalHelperText: PropTypes.node,
  HelperText: PropTypes.node,
  suffixButtonType: PropTypes.oneOf([
    'date',
    'password',
    'dropdown',
    'timePicker',
  ]),
  suffixIsToggle: PropTypes.bool,
  suffixOnClick: PropTypes.func,
  backgroundColor: PropTypes.oneOf(['neutral', 'secondary']),
  value: PropTypes.string,
  setValue: PropTypes.func,
  setIsCritical: PropTypes.func,
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default InputField;
